import { useState } from 'react';
import {
  LayoutDashboard,
  FileText,
  Users,
  UserCog,
  Menu,
  X,
  Home,
  Settings,
  Globe,
  HelpCircle,
  Navigation,
  PanelTop,
  PanelBottom,
  ChevronDown,
  ChevronRight,
  Mail,
  BookOpen,
  Brain,
  Sparkles,
  BarChart3,
  Share2,
  CreditCard,
  Package,
  Receipt,
  HeartHandshake,
  Shield,
  SendHorizontal,
} from 'lucide-react';
import { BrandLogo } from './BrandLogo';

export type AdminView = 'dashboard' | 'pages' | 'home' | 'seo' | 'linkPreview' | 'faq' | 'blog' | 'businessContext' | 'websiteRequests' | 'contactMessages' | 'newsletter' | 'ambassadorApplications' | 'corporateInquiries' | 'monthlyImpactCampaigns' | 'scanRequests' | 'customers' | 'donors' | 'users' | 'settings' | 'gtm' | 'llmManagement' | 'navigation' | 'headerConfig' | 'footerConfig' | 'stripeSettings' | 'stripeProducts' | 'stripeInvoices';

interface AdminSidebarProps {
  currentView: AdminView;
  onViewChange: (view: AdminView) => void;
  onBackToHome: () => void;
  userRole: 'root' | 'admin' | 'moderator';
  /** When set, non-root admins see only these modules (module-based access). Root ignores this. */
  allowedModules?: AdminView[] | null;
  userName?: string;
  userPhoto?: string | null;
  onOpenProfile?: () => void;
}

const HomeIcon = Home;

const menuItems: Array<{
  id: AdminView;
  label: string;
  icon: React.ReactNode;
  availableFor: ('root' | 'admin' | 'moderator')[];
  group?: string;
}> = [
  { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} />, availableFor: ['root', 'admin'] },
  { id: 'blog', label: 'Blog Articles', icon: <BookOpen size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'businessContext', label: 'AI Context Library', icon: <Brain size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'pages', label: 'CMS Pages', icon: <FileText size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'home', label: 'Set Home Page', icon: <HomeIcon size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'navigation', label: 'Navigation Manager', icon: <Navigation size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'headerConfig', label: 'Header Config', icon: <PanelTop size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'footerConfig', label: 'Footer Config', icon: <PanelBottom size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'seo', label: 'SEO', icon: <Globe size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'linkPreview', label: 'Link Preview', icon: <Share2 size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'faq', label: 'FAQ', icon: <HelpCircle size={16} />, availableFor: ['root', 'admin'], group: 'Content' },
  { id: 'newsletter', label: 'Newsletter Subscribers', icon: <Mail size={16} />, availableFor: ['root', 'admin'], group: 'Leads' },
  { id: 'monthlyImpactCampaigns', label: 'Monthly Impact Campaigns', icon: <SendHorizontal size={16} />, availableFor: ['root', 'admin'], group: 'Leads' },
  { id: 'ambassadorApplications', label: 'Warrior Applications', icon: <Shield size={16} />, availableFor: ['root', 'admin'], group: 'Leads' },
  { id: 'corporateInquiries', label: 'Corporate Inquiries', icon: <Shield size={16} />, availableFor: ['root', 'admin'], group: 'Leads' },
  { id: 'customers', label: 'Customers', icon: <Users size={16} />, availableFor: ['root', 'admin'], group: 'People' },
  { id: 'donors', label: 'Donors', icon: <HeartHandshake size={16} />, availableFor: ['root', 'admin'], group: 'People' },
  { id: 'users', label: 'Admin Users', icon: <UserCog size={16} />, availableFor: ['root'], group: 'People' },
  { id: 'stripeSettings', label: 'Stripe Settings', icon: <CreditCard size={16} />, availableFor: ['root'], group: 'Billing' },
  { id: 'stripeProducts', label: 'Products & Pricing', icon: <Package size={16} />, availableFor: ['root', 'admin'], group: 'Billing' },
  { id: 'stripeInvoices', label: 'Invoices', icon: <Receipt size={16} />, availableFor: ['root', 'admin'], group: 'Billing' },
  { id: 'llmManagement', label: 'LLM Models', icon: <Sparkles size={16} />, availableFor: ['root', 'admin'], group: 'System' },
  { id: 'gtm', label: 'Google Tag Manager', icon: <BarChart3 size={16} />, availableFor: ['root', 'admin'], group: 'System' },
  { id: 'settings', label: 'Settings & Branding', icon: <Settings size={16} />, availableFor: ['root', 'admin'], group: 'System' },
];

/** Ordered group definitions with display labels. Leads first (after Dashboard). */
const GROUP_ORDER = ['Leads', 'Content', 'People', 'Billing', 'System'] as const;
type GroupName = typeof GROUP_ORDER[number];

/** Modules that can be assigned to non-root admins (User Management is root-only). */
export const ASSIGNABLE_ADMIN_MODULES: Array<{ id: AdminView; label: string }> = menuItems
  .filter((item) => item.id !== 'users' && item.id !== 'stripeSettings')
  .map((item) => ({ id: item.id, label: item.label }));

export const AdminSidebar = ({
  currentView,
  onViewChange,
  onBackToHome,
  userRole,
  allowedModules = null,
  userName,
  userPhoto,
  onOpenProfile,
}: AdminSidebarProps) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const filteredMenuItems =
    userRole === 'root'
      ? menuItems
      : allowedModules && allowedModules.length > 0
        ? menuItems.filter(
            (item) =>
              item.id !== 'users' &&
              item.availableFor.includes(userRole) &&
              allowedModules?.includes(item.id)
          )
        : menuItems.filter((item) => item.availableFor.includes(userRole));

  const standaloneItems = filteredMenuItems.filter((item) => !item.group);

  const groupedItems = GROUP_ORDER.reduce<Record<GroupName, typeof menuItems>>((acc, group) => {
    acc[group] = filteredMenuItems.filter((item) => item.group === group);
    return acc;
  }, {} as Record<GroupName, typeof menuItems>);

  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const isGroupCollapsed = (group: string) => !!collapsedGroups[group];

  const handleViewChange = (view: AdminView) => {
    onViewChange(view);
    setIsMobileOpen(false);
  };

  const navItemClass = (isActive: boolean) =>
    `w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left transition-colors duration-150 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-900 ${
      isActive
        ? 'bg-blue-600 text-white'
        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
    }`;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-blue-600 text-white p-2 rounded-lg shadow-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label="Open admin menu"
      >
        <Menu size={22} />
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40
          w-56 bg-gray-900 text-white
          transform transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col h-screen shadow-xl
        `}
        aria-label="Admin navigation"
      >
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-800 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <BrandLogo
                variant="placeholder"
                onDark
                label="GSGO"
                className="mb-0.5"
                alt="GSGO"
              />
              <p className="text-xs font-semibold text-gray-400 tracking-wide uppercase">Admin Panel</p>
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white transition-colors"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* User Info */}
          {userName && (
            onOpenProfile ? (
              <button
                onClick={onOpenProfile}
                className="w-full flex items-center gap-2.5 pt-3 border-t border-gray-800 hover:bg-gray-800/50 rounded-lg px-2 py-1.5 -mx-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 cursor-pointer"
                aria-label="Open profile"
              >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  {userPhoto ? (
                    <img src={userPhoto} alt={userName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-semibold text-xs">{userName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p className="font-medium text-xs truncate">{userName}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {userRole === 'root' ? 'Root Admin' : userRole === 'admin' ? 'Admin' : 'Moderator'}
                  </p>
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-2.5 pt-3 border-t border-gray-800">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  {userPhoto ? (
                    <img src={userPhoto} alt={userName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-semibold text-xs">{userName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-xs truncate">{userName}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {userRole === 'root' ? 'Root Admin' : userRole === 'admin' ? 'Admin' : 'Moderator'}
                  </p>
                </div>
              </div>
            )
          )}
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 min-h-0 space-y-0.5">
          {/* Standalone items (Dashboard) */}
          {standaloneItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleViewChange(item.id)}
              className={navItemClass(currentView === item.id)}
              aria-current={currentView === item.id ? 'page' : undefined}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}

          {/* Grouped sections */}
          {GROUP_ORDER.map((group) => {
            const items = groupedItems[group];
            if (!items || items.length === 0) return null;

            const isCollapsed = isGroupCollapsed(group);
            const hasActiveItem = items.some((item) => item.id === currentView);

            return (
              <div key={group} className="pt-1">
                {/* Group header toggle */}
                <button
                  onClick={() => toggleGroup(group)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-900 group ${
                    hasActiveItem && isCollapsed
                      ? 'text-blue-400'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                  aria-expanded={!isCollapsed}
                >
                  <span className="text-xs font-semibold tracking-wider uppercase">{group}</span>
                  {isCollapsed
                    ? <ChevronRight size={12} className="flex-shrink-0" />
                    : <ChevronDown size={12} className="flex-shrink-0" />
                  }
                </button>

                {/* Group items */}
                {!isCollapsed && (
                  <div className="mt-0.5 space-y-0.5">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleViewChange(item.id)}
                        className={navItemClass(currentView === item.id)}
                        aria-current={currentView === item.id ? 'page' : undefined}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-2.5 py-3 border-t border-gray-800 shrink-0">
          <button
            onClick={onBackToHome}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-150 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-900"
          >
            <Home size={16} />
            <span>Back to Home</span>
          </button>
        </div>
      </aside>
    </>
  );
};
